/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';
import _ from 'lodash';
import { Header, Icon } from 'semantic-ui-react';

/**
 * Gets status color based on usage percentage
 */
function getStatusColor(percentUsed) {
  if (percentUsed > 100) return '#e61e2a'; // Red - over budget
  if (percentUsed >= 90) return '#ea580c'; // Orange - critical
  if (percentUsed >= 75) return '#fac800'; // Amber - warning
  return '#10b981'; // Green - healthy
}

/**
 * Gets status label based on usage percentage
 */
function getStatusLabel(percentUsed) {
  if (percentUsed > 100) return 'Over Budget';
  if (percentUsed >= 90) return 'Critical';
  if (percentUsed >= 75) return 'Warning';
  return 'Healthy';
}

/**
 * Circular progress ring component
 */
function ProgressRing({ percent, size = 120, strokeWidth = 10, color }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const cappedPercent = Math.min(percent, 100);
  const offset = circumference - (cappedPercent / 100) * circumference;

  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      {/* Background circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#e5e7eb"
        strokeWidth={strokeWidth}
      />
      {/* Progress circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.5s ease' }}
      />
    </svg>
  );
}


const cardContainerStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(400px, 100%), 1fr))',
  gap: '24px',
  padding: '8px 0',
};

const cardStyle = {
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  borderRadius: '16px',
  padding: '24px',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  border: '1px solid #e2e8f0',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
};

const cardHoverStyle = {
  transform: 'translateY(-4px)',
  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '20px',
};

const projectNameStyle = {
  fontSize: '1.1em',
  fontWeight: '600',
  color: '#1e293b',
  margin: 0,
  lineHeight: '1.3',
};

const descriptionStyle = {
  fontSize: '0.85em',
  color: '#64748b',
  marginTop: '4px',
};

const statusBadgeStyle = (color) => ({
  backgroundColor: `${color}15`,
  color: color,
  padding: '4px 12px',
  borderRadius: '20px',
  fontSize: '0.75em',
  fontWeight: '600',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  whiteSpace: 'nowrap',
  flexShrink: 0,
});

const mainContentStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '24px',
  flexWrap: 'wrap',
  justifyContent: 'center',
};

const ringContainerStyle = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const ringTextStyle = {
  position: 'absolute',
  textAlign: 'center',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  paddingTop: '1em',
};

const percentTextStyle = {
  fontSize: '1.5em',
  fontWeight: '700',
  color: '#1e293b',
  lineHeight: '1',
};

const percentLabelStyle = {
  fontSize: '0.7em',
  color: '#64748b',
};

const statsContainerStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const statRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px 12px',
  backgroundColor: '#f8fafc',
  borderRadius: '8px',
};

const statLabelStyle = {
  fontSize: '0.85em',
  color: '#64748b',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
};

const statValueStyle = {
  fontSize: '0.95em',
  fontWeight: '600',
  color: '#1e293b',
};

const forecastContainerStyle = {
  marginTop: '16px',
  padding: '12px 16px',
  backgroundColor: '#F1F2F0', // 50% tint of #E3E5E0
  borderRadius: '10px',
  border: '1px solid #E3E5E0',
};

const forecastHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '8px',
};

const forecastTitleStyle = {
  fontSize: '0.8em',
  fontWeight: '600',
  color: '#5c5e5a', // darker shade for readability
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const forecastValueStyle = {
  fontSize: '1.2em',
  fontWeight: '700',
  color: '#3d3e3c', // darker shade for emphasis
};

const forecastNoteStyle = {
  fontSize: '0.75em',
  color: '#6b6d69', // 75% tint area
  marginTop: '4px',
};

const missingBudgetCardStyle = {
  ...cardStyle,
  background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
  border: '1px solid #fecaca',
};

const missingBudgetTextStyle = {
  textAlign: 'center',
  padding: '20px',
};

class BudgetCards extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hoveredCard: null,
    };
  }

  handleMouseEnter = (projectId) => {
    this.setState({ hoveredCard: projectId });
  };

  handleMouseLeave = () => {
    this.setState({ hoveredCard: null });
  };

  formatCurrency(value) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  renderMissingBudgetCard(projectId, description) {
    const { hoveredCard } = this.state;
    const isHovered = hoveredCard === projectId;
    const noFundingColor = '#6b7280'; // Gray color for no funding

    return (
      <div
        key={projectId}
        style={{
          ...cardStyle,
          ...(isHovered ? cardHoverStyle : {}),
        }}
        onMouseEnter={() => this.handleMouseEnter(projectId)}
        onMouseLeave={this.handleMouseLeave}
      >
        {/* Header */}
        <div style={headerStyle}>
          <div>
            <h3 style={projectNameStyle}>{projectId}</h3>
            {description && <p style={descriptionStyle}>{description}</p>}
          </div>
          <span style={statusBadgeStyle(noFundingColor)}>No Funding</span>
        </div>

        {/* Main content */}
        <div style={mainContentStyle}>
          {/* Progress ring */}
          <div style={ringContainerStyle}>
            <ProgressRing percent={0} color={noFundingColor} />
            <div style={ringTextStyle}>
              <div style={percentTextStyle}>--%</div>
              <div style={percentLabelStyle}>used</div>
            </div>
          </div>

          {/* Stats */}
          <div style={statsContainerStyle}>
            <div style={statRowStyle}>
              <span style={statLabelStyle}>
                <Icon name="dollar sign" size="small" />
                Awarded Credits
              </span>
              <span style={statValueStyle}>{this.formatCurrency(0)}</span>
            </div>
            <div style={statRowStyle}>
              <span style={statLabelStyle}>
                <Icon name="chart line" size="small" />
                Consumed
              </span>
              <span style={statValueStyle}>{this.formatCurrency(0)}</span>
            </div>
            <div style={statRowStyle}>
              <span style={statLabelStyle}>
                <Icon name="money bill alternate outline" size="small" />
                Remaining
              </span>
              <span style={statValueStyle}>{this.formatCurrency(0)}</span>
            </div>
          </div>
        </div>

        {/* Contact notice */}
        <div style={{ 
          marginTop: '16px', 
          padding: '10px 14px', 
          backgroundColor: '#fce8e9', // light tint of #e61e2a
          borderRadius: '8px',
          fontSize: '0.85em',
          color: '#b91c26',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <Icon name="info circle" style={{ color: '#e61e2a', lineHeight: 1, height: '1em' }} />
          Contact RACE to assign project funding.
        </div>
      </div>
    );
  }

  renderBudgetCard(projectId, budgetData) {
    const { budgetUsage, budgetLimit, budgetForecast, description } = budgetData;
    const { hoveredCard } = this.state;

    // Handle missing budget
    if (budgetLimit === -1 || budgetUsage === -1) {
      return this.renderMissingBudgetCard(projectId, description);
    }

    const budgetUsageValue = parseFloat(budgetUsage);
    const budgetLimitValue = parseFloat(budgetLimit);
    const budgetForecastValue = budgetForecast ? parseFloat(budgetForecast) : null;

    if (isNaN(budgetUsageValue) || isNaN(budgetLimitValue)) {
      return null;
    }

    const percentUsed = (budgetUsageValue / budgetLimitValue) * 100;
    const remaining = budgetLimitValue - budgetUsageValue;
    const statusColor = getStatusColor(percentUsed);
    const statusLabel = getStatusLabel(percentUsed);
    const isHovered = hoveredCard === projectId;

    return (
      <div
        key={projectId}
        style={{
          ...cardStyle,
          ...(isHovered ? cardHoverStyle : {}),
        }}
        onMouseEnter={() => this.handleMouseEnter(projectId)}
        onMouseLeave={this.handleMouseLeave}
      >
        {/* Header */}
        <div style={headerStyle}>
          <div>
            <h3 style={projectNameStyle}>{projectId}</h3>
            {description && <p style={descriptionStyle}>{description}</p>}
          </div>
          <span style={statusBadgeStyle(statusColor)}>{statusLabel}</span>
        </div>

        {/* Main content */}
        <div style={mainContentStyle}>
          {/* Progress ring */}
          <div style={ringContainerStyle}>
            <ProgressRing percent={percentUsed} color={statusColor} />
            <div style={ringTextStyle}>
              <div style={percentTextStyle}>{percentUsed.toFixed(0)}%</div>
              <div style={percentLabelStyle}>used</div>
            </div>
          </div>

          {/* Stats */}
          <div style={statsContainerStyle}>
            <div style={statRowStyle}>
              <span style={statLabelStyle}>
                <Icon name="dollar sign" size="small" />
                Awarded Credits
              </span>
              <span style={statValueStyle}>{this.formatCurrency(budgetLimitValue)}</span>
            </div>
            <div style={statRowStyle}>
              <span style={statLabelStyle}>
                <Icon name="chart line" size="small" />
                Consumed
              </span>
              <span style={{ ...statValueStyle, color: statusColor }}>{this.formatCurrency(budgetUsageValue)}</span>
            </div>
            <div style={statRowStyle}>
              <span style={statLabelStyle}>
                <Icon name="money bill alternate outline" size="small" />
                Remaining
              </span>
              <span style={{ ...statValueStyle, color: remaining < 0 ? '#dc2626' : '#10b981' }}>
                {this.formatCurrency(remaining)}
              </span>
            </div>
          </div>
        </div>

        {/* Forecast section - admin only */}
        {this.props.isAdmin && budgetForecastValue && !isNaN(budgetForecastValue) && (
          <div style={forecastContainerStyle}>
            <div style={forecastHeaderStyle}>
              <Icon name="chart bar" style={{ color: '#5c5e5a', lineHeight: 1, height: '1em' }} />
              <span style={forecastTitleStyle}>Project Forecast</span>
            </div>
            <div style={forecastValueStyle}>{this.formatCurrency(budgetForecastValue)}</div>
            <div style={forecastNoteStyle}>
              Projected spend by end of year
              {budgetForecastValue > budgetLimitValue && (
                <span style={{ color: '#dc2626', fontWeight: '600' }}> — Exceeds budget!</span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  render() {
    const { budgets } = this.props;

    if (_.isEmpty(budgets)) {
      return null;
    }

    // Sort projects: valid budgets first (by usage %), then missing budgets
    const projectIds = Object.keys(budgets).sort((a, b) => {
      const aLimit = budgets[a].budgetLimit;
      const bLimit = budgets[b].budgetLimit;

      // Put missing budgets at the end
      if (aLimit === -1 && bLimit !== -1) return 1;
      if (bLimit === -1 && aLimit !== -1) return -1;
      if (aLimit === -1 && bLimit === -1) return 0;

      const aUsage = parseFloat(budgets[a].budgetUsage) / parseFloat(aLimit);
      const bUsage = parseFloat(budgets[b].budgetUsage) / parseFloat(bLimit);
      return bUsage - aUsage;
    });

    return (
      <div className={this.props.hideTitle ? '' : 'mb3'}>
        {!this.props.hideTitle && (
          <div className="mb2 flex">
            <Header as="h3" className="color-rmit-blue mt1 mb0 flex-auto">
              <Icon name="credit card" className="align-top" />
              <Header.Content className="left-align">My Projects</Header.Content>
            </Header>
          </div>
        )}
        <div style={cardContainerStyle}>
          {projectIds.map(projectId => this.renderBudgetCard(projectId, budgets[projectId]))}
        </div>
      </div>
    );
  }
}

export default BudgetCards;
